import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useDispatch } from "react-redux";

import {
  pedidosApi,
  couponApi,
  cardsApi,
  clientesApi,
} from "page/OrderResumePag/_services/api";
import { useUserData } from "hooks/UserContext";
import {
  getCustomerFromMemory,
  setCustomerInMemory,
} from "lib/sharedSessionMemory";
import { useApp } from "contexts/AppContext";
import { useFeatureFlag, usePaymentFlags } from "contexts/FeatureFlagProvider";
import { useCardManagement } from "components/CardManagement";
import notify from "components/UI/toast";
import { roundCurrency } from "lib";
import { removeItemCart } from "Redux/cartSlice";
import {
  trackAddPaymentInfoMarketplaceItems,
  trackPurchaseMarketplaceItems,
} from "utils/gtm";

const channel = process.env.REACT_APP_DEFAULT_CHANNEL;
const MONEY_THRESHOLD = 0.05;

function getSliderLimits({
  totalMoney,
  totalPoints,
  balance,
  minPoints,
  factor,
}) {
  const maxPointsFromBalance = Math.min(balance, totalPoints);
  const pointsDeficit = Math.max(0, totalPoints - maxPointsFromBalance);
  const minMoneyRequired = factor > 0 ? pointsDeficit / factor : totalMoney;

  let maxMoneyAllowed = totalMoney;
  if (minPoints > 0 && totalPoints > 0) {
    const minPointsInMoney = factor > 0 ? minPoints / factor : 0;
    maxMoneyAllowed = Math.max(0, totalMoney - minPointsInMoney);
  }

  const minSlider =
    totalMoney > 0 ? (minMoneyRequired / totalMoney) * 100 : 0;
  const maxSlider =
    totalMoney > 0 ? (maxMoneyAllowed / totalMoney) * 100 : 100;

  return { minSlider, maxSlider, maxPointsFromBalance };
}

function computePaymentFromMoneyPct(moneyPct, { totalMoney, totalPoints, factor }) {
  let moneyValue = (moneyPct / 100) * totalMoney;

  if (moneyValue < MONEY_THRESHOLD && moneyValue > 0) {
    moneyValue = 0;
  }

  const isFullPoints = moneyPct === 0 || moneyValue === 0;
  const pointsValue = isFullPoints
    ? totalPoints
    : totalPoints - Math.round(moneyValue * factor);

  return {
    pointsValue,
    moneyValue: parseFloat(roundCurrency(moneyValue)),
  };
}

function computePaymentFromPoints(targetPoints, ctx) {
  const limits = getSliderLimits(ctx);
  const minPoints =
    ctx.minPoints > 0 && ctx.totalPoints >= ctx.minPoints ? ctx.minPoints : 0;

  let points = Math.max(minPoints, Math.min(limits.maxPointsFromBalance, targetPoints));

  let moneyPct =
    ctx.totalPoints > 0
      ? ((ctx.totalPoints - points) / ctx.totalPoints) * 100
      : 100;
  moneyPct = Math.max(
    limits.minSlider,
    Math.min(limits.maxSlider, moneyPct),
  );

  return computePaymentFromMoneyPct(moneyPct, ctx);
}

function computeInitialPoints(ctx) {
  const { balance, totalPoints } = ctx;
  if (totalPoints <= 0) {
    return { pointsValue: 0, moneyValue: parseFloat(roundCurrency(ctx.totalMoney)) };
  }

  let moneyPct = 0;
  if (balance < totalPoints) {
    moneyPct = ((totalPoints - balance) / totalPoints) * 100;
  }
  moneyPct = Math.min(moneyPct, 100);

  const limits = getSliderLimits(ctx);
  if (moneyPct > limits.maxSlider && ctx.minPoints > 0) {
    moneyPct = limits.maxSlider;
  }

  let result = computePaymentFromMoneyPct(moneyPct, ctx);

  const wasEnforcedToMin =
    ctx.minPoints > 0 &&
    result.pointsValue < ctx.minPoints &&
    totalPoints >= ctx.minPoints;
  if (wasEnforcedToMin) {
    const enforcedPoints = Math.min(ctx.minPoints, totalPoints);
    moneyPct =
      totalPoints > 0
        ? ((totalPoints - enforcedPoints) / totalPoints) * 100
        : 100;
    result = computePaymentFromMoneyPct(moneyPct, ctx);
  }

  return result;
}

// O endpoint `v2/checkout/resumo/{channel}` retorna `cart` agrupado por brand UUID,
// não como `{ items: [] }`. Esta função achata para o consumidor.
function flattenCart(cartData) {
  if (!cartData) return { items: [], shipping_by_brand: {} };
  if (Array.isArray(cartData.items)) return cartData;
  const allItems = [];
  const shippingByBrand = {};
  Object.entries(cartData).forEach(([brandUuid, cartInfo]) => {
    if (cartInfo?.items?.length) {
      cartInfo.items.forEach((it) => allItems.push(it));
      if (cartInfo.deliveryOptions) {
        shippingByBrand[brandUuid] = {
          shipping_amount: cartInfo.deliveryOptions.shippingPrice || 0,
          shipping_point: cartInfo.deliveryOptions.shippingPoints || 0,
          shipping_deadline:
            cartInfo.deliveryOptions.estimatedBusinessDays || 0,
        };
      }
    }
  });
  return { items: allItems, shipping_by_brand: shippingByBrand };
}

export function useMarketplaceCheckout() {
  const { user, addressShipping, updateUserData } = useUserData();
  const { whitelabel } = useApp();
  const paymentFlags = usePaymentFlags();
  const couponsEnabled = useFeatureFlag("coupons");

  const rawDisplayMode = whitelabel?.config?.payment_mode || "money";
  const displayMode = paymentFlags?.points === false ? "money" : rawDisplayMode;

  const configData = whitelabel?.config || {};
  const fatorFrete = configData[`fator_${channel}_frete`] / 10000 || 0;
  const minPoints = Number(configData?.[`${channel}_min_value`]) || 0;
  const factor = fatorFrete > 0 ? 1 / fatorFrete : 1;

  const cep =
    addressShipping?.cep ||
    addressShipping?.zipCode ||
    user?.addresses?.[0]?.cep;

  const [loading, setLoading] = useState(true);
  const [loadingCheckout, setLoadingCheckout] = useState(false);
  const [dataCheckout, setDataCheckout] = useState({
    cart: { items: [] },
    summary: {},
  });

  const [coupon, setCoupon] = useState("");
  const [couponInfo, setCouponInfo] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [loadingCoupon, setLoadingCoupon] = useState(false);

  const fetchSummary = useCallback(
    async (silent = false) => {
      if (!user?.uuid) return;
      if (!silent) setLoading(true);
      try {
        const response = await pedidosApi.get(
          `v2/checkout/resumo/${channel}`,
          { params: { customerUuid: user.uuid, cep, fator: fatorFrete } },
        );
        const data = response.data?.data || response.data || {};
        const normalized = {
          ...data,
          cart: flattenCart(data.cart),
        };
        setDataCheckout(normalized);
      } catch (e) {
        notify("Erro ao carregar o checkout.", "error");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [user?.uuid, cep, fatorFrete],
  );

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    const c = dataCheckout?.summary?.coupon;
    if (c) {
      setCoupon(c.code || c.name || "");
      setCouponInfo(c);
      setCouponError("");
    } else {
      setCouponInfo(null);
    }
  }, [dataCheckout]);

  const applyCoupon = useCallback(async () => {
    const code = coupon?.trim();
    if (!code || !couponsEnabled) return;
    setLoadingCoupon(true);
    setCouponError("");
    try {
      const { data, status } = await couponApi.post(
        `/api/coupon/apply/${channel}`,
        {
          customerUuid: user.uuid,
          cep: cep || "",
          coupon: code,
          fator: fatorFrete,
        },
      );
      if (status >= 200 && status < 300) {
        await fetchSummary(true);
      } else {
        setCouponError(data?.message || "Cupom inválido.");
      }
    } catch (e) {
      setCouponError(
        e?.response?.data?.message || "Ocorreu um erro ao aplicar o cupom.",
      );
    } finally {
      setLoadingCoupon(false);
    }
  }, [coupon, couponsEnabled, user?.uuid, cep, fatorFrete, fetchSummary]);

  const removeCoupon = useCallback(async () => {
    if (!couponInfo) return;
    setLoadingCoupon(true);
    try {
      await couponApi.post(`/api/coupon/remove/${channel}`, {
        customerUuid: user.uuid,
        cep: cep || "",
        fator: fatorFrete,
      });
      setCoupon("");
      setCouponInfo(null);
      setCouponError("");
      await fetchSummary(true);
    } catch (e) {
      notify("Erro ao remover cupom.", "error");
    } finally {
      setLoadingCoupon(false);
    }
  }, [couponInfo, user?.uuid, cep, fatorFrete, fetchSummary]);

  const productPoints = useMemo(
    () =>
      Math.round(
        dataCheckout.cart?.items?.reduce(
          (acc, item) =>
            acc + (item.price?.points?.offer || 0) * (item.quantity || 0),
          0,
        ) || 0,
      ),
    [dataCheckout.cart?.items],
  );

  const shippingPoints = useMemo(() => {
    const summaryShippingPoints = parseFloat(
      dataCheckout.summary?.shippingPoints,
    );
    if (summaryShippingPoints > 0) {
      return Math.round(summaryShippingPoints);
    }

    const shippingBRL = parseFloat(dataCheckout.summary?.shipping) || 0;
    if (shippingBRL > 0 && fatorFrete > 0) {
      return Math.round(shippingBRL * factor);
    }

    return 0;
  }, [
    dataCheckout.summary?.shippingPoints,
    dataCheckout.summary?.shipping,
    fatorFrete,
    factor,
  ]);

  const totalPoints = productPoints + shippingPoints;

  const discountInPoints = useMemo(() => {
    const discount = parseFloat(dataCheckout.summary?.discount) || 0;
    if (discount <= 0 || fatorFrete <= 0) {
      return 0;
    }
    return Math.round(discount * factor);
  }, [dataCheckout.summary?.discount, fatorFrete, factor]);

  const effectiveTotalPoints = Math.max(0, totalPoints - discountInPoints);
  const totalEmReais = Number(dataCheckout?.summary?.total || 0);

  const financialSummary = useMemo(() => {
    const summary = dataCheckout?.summary || {};
    const subtotalRaw = Number(summary.subtotal || 0);
    const shippingRaw = Number(summary.shipping || 0);
    const discountRaw = parseFloat(summary.discount) || 0;

    return {
      subtotalMoney: parseFloat(roundCurrency(subtotalRaw)),
      subtotalPoints: productPoints,
      shippingMoney: parseFloat(roundCurrency(shippingRaw)),
      shippingPoints: shippingPoints,
      totalMoney: parseFloat(roundCurrency(totalEmReais)),
      totalPoints: effectiveTotalPoints,
      discountMoney: parseFloat(roundCurrency(discountRaw)),
      discountPoints: discountInPoints,
    };
  }, [
    dataCheckout?.summary,
    productPoints,
    shippingPoints,
    effectiveTotalPoints,
    totalEmReais,
    discountInPoints,
  ]);

  const sliderContext = useMemo(
    () => ({
      totalMoney: totalEmReais,
      totalPoints: effectiveTotalPoints,
      balance: Math.trunc(Number(user?.balance?.data || 0)),
      minPoints,
      factor,
    }),
    [totalEmReais, effectiveTotalPoints, user?.balance?.data, minPoints, factor],
  );

  const userBalance = sliderContext.balance;

  const sliderLimits = useMemo(
    () => getSliderLimits(sliderContext),
    [sliderContext],
  );

  const pontosDisponiveis = sliderLimits.maxPointsFromBalance;
  const minPontosSlider =
    minPoints > 0 && effectiveTotalPoints >= minPoints ? minPoints : 0;

  const [pontosValue, setPontosValue] = useState(0);
  const [sliderPontos, setSliderPontos] = useState(0);
  const paymentInfoTimer = useRef(null);

  useEffect(
    () => () => {
      if (paymentInfoTimer.current) clearTimeout(paymentInfoTimer.current);
    },
    [],
  );

  const resolvePointsTarget = useCallback(
    (targetPoints, { notifyOnClamp = false } = {}) => {
      const limits = getSliderLimits(sliderContext);
      const minPts =
        sliderContext.minPoints > 0 &&
        sliderContext.totalPoints >= sliderContext.minPoints
          ? sliderContext.minPoints
          : 0;

      let adjustedTarget = targetPoints;

      if (adjustedTarget > limits.maxPointsFromBalance) {
        if (notifyOnClamp) {
          const balanceFormatted = sliderContext.balance.toLocaleString("pt-BR");
          notify(
            `Não é possível selecionar mais pontos. Você possui ${balanceFormatted} pontos disponíveis. O valor máximo foi ajustado automaticamente.`,
            "warning",
          );
        }
        adjustedTarget = limits.maxPointsFromBalance;
      }

      if (adjustedTarget < minPts) {
        if (notifyOnClamp) {
          const minFormatted = minPts.toLocaleString("pt-BR");
          notify(
            `É necessário selecionar pelo menos ${minFormatted} pontos. O valor mínimo foi ajustado automaticamente.`,
            "warning",
          );
        }
        adjustedTarget = minPts;
      }

      return computePaymentFromPoints(adjustedTarget, sliderContext);
    },
    [sliderContext],
  );

  useEffect(() => {
    if (displayMode === "money") {
      setPontosValue(0);
      setSliderPontos(0);
      return;
    }

    if (displayMode === "both" || displayMode === "points") {
      if (sliderContext.balance <= 0) {
        setPontosValue(0);
        setSliderPontos(0);
        return;
      }
      const initial = computeInitialPoints(sliderContext);
      setPontosValue(initial.pointsValue);
      setSliderPontos(initial.pointsValue);
    }
  }, [
    displayMode,
    sliderContext.totalMoney,
    sliderContext.totalPoints,
    sliderContext.balance,
    sliderContext.minPoints,
  ]);

  const sliderReais = useMemo(() => {
    if (displayMode === "money" || effectiveTotalPoints <= 0) {
      return parseFloat(roundCurrency(totalEmReais));
    }

    return resolvePointsTarget(sliderPontos).moneyValue;
  }, [
    displayMode,
    sliderPontos,
    resolvePointsTarget,
    effectiveTotalPoints,
    totalEmReais,
  ]);

  const reaisValue = useMemo(() => {
    if (displayMode === "money" || effectiveTotalPoints <= 0) {
      return parseFloat(roundCurrency(totalEmReais));
    }

    return resolvePointsTarget(pontosValue).moneyValue;
  }, [
    displayMode,
    pontosValue,
    resolvePointsTarget,
    effectiveTotalPoints,
    totalEmReais,
  ]);

  const reaisRestante = reaisValue;
  const moneyFromPoints = Math.max(0, totalEmReais - reaisValue);

  const handlePontosPreview = useCallback(
    (targetPoints) => {
      const { pointsValue } = resolvePointsTarget(targetPoints);
      setSliderPontos(pointsValue);
    },
    [resolvePointsTarget],
  );

  const handlePontosCommit = useCallback(
    (targetPoints) => {
      const { pointsValue, moneyValue } = resolvePointsTarget(targetPoints, {
        notifyOnClamp: true,
      });
      setPontosValue(pointsValue);
      setSliderPontos(pointsValue);

      if (paymentInfoTimer.current) clearTimeout(paymentInfoTimer.current);
      paymentInfoTimer.current = setTimeout(() => {
        const paymentType =
          moneyValue > 0 && pointsValue > 0
            ? "cartao-de-credito+pontos"
            : moneyValue > 0
              ? "cartao-de-credito"
              : "pontos";
        trackAddPaymentInfoMarketplaceItems(
          dataCheckout.cart?.items || [],
          paymentType,
        );
      }, 500);
    },
    [resolvePointsTarget, dataCheckout.cart?.items],
  );

  // Slider é exibido quando o canal aceita pontos E aceita alguma forma de
  // pagamento em reais (cartão ou Pix). Em canais como Safra, `payment_mode`
  // pode estar como "points" mas o usuário ainda pode complementar com cartão.
  const hasBrlMethod =
    paymentFlags?.card !== false || paymentFlags?.pix !== false;
  const showPointsSlider =
    paymentFlags?.points === true &&
    displayMode !== "money" &&
    hasBrlMethod &&
    effectiveTotalPoints > 0;

  const [paymentMethod, setPaymentMethod] = useState(null); // 'pix' | 'card' | null

  useEffect(() => {
    if (reaisRestante === 0 && paymentMethod !== null) {
      setPaymentMethod(null);
    }
  }, [reaisRestante, paymentMethod]);

  const [cards, setCards] = useState([]);
  const [refreshCards, setRefreshCards] = useState(0);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [installments, setInstallments] = useState([]);
  const [installmentsSelected, setInstallmentsSelected] = useState(null);

  const effectiveAddresses = user?.addresses?.length
    ? user.addresses
    : addressShipping
      ? [addressShipping]
      : [];

  const cardMgmt = useCardManagement({
    cards,
    setCards,
    setRefreshCards,
    addresses: effectiveAddresses,
  });

  useEffect(() => {
    if (reaisRestante > 0 && selectedCardId && paymentMethod === null) {
      setPaymentMethod("card");
    }
  }, [reaisRestante, selectedCardId, paymentMethod]);

  useEffect(() => {
    if (!user?.cpf) {
      setCards([]);
      return;
    }
    cardsApi
      .get(`/v1/customer/card`)
      .then((r) => setCards(r.data?.data || []))
      .catch(() => setCards([]));
  }, [user?.cpf, refreshCards]);

  useEffect(() => {
    if (paymentMethod !== "card" || !selectedCardId || reaisValue <= 0) {
      setInstallments([]);
      setInstallmentsSelected(null);
      return;
    }
    pedidosApi
      .get(`v1/installment/options/${channel}`, {
        params: {
          customerUuid: user.uuid,
          amount: reaisValue,
          installmentType: "produto",
        },
      })
      .then((r) => setInstallments(Object.values(r.data?.data || {})))
      .catch(() => setInstallments([]));
  }, [paymentMethod, selectedCardId, reaisValue, user?.uuid]);

  const hasInsufficientPoints = useMemo(() => {
    if (displayMode === "money") return false;
    if (minPoints <= 0) return false;
    return userBalance < minPoints;
  }, [displayMode, minPoints, userBalance]);

  const canFinalize = useMemo(() => {
    if (loadingCheckout) return false;

    if (hasInsufficientPoints) return false;

    // Pontos/cupom cobrem 100%
    if (reaisRestante === 0) return true;

    // PIX selected and there's BRL to pay
    if (paymentMethod === "pix") return reaisRestante > 0;

    // Cartão requires card + installment
    if (paymentMethod === "card") {
      return Boolean(selectedCardId) && Boolean(installmentsSelected);
    }

    // No payment method picked while BRL remains
    return false;
  }, [
    loadingCheckout,
    hasInsufficientPoints,
    reaisRestante,
    paymentMethod,
    selectedCardId,
    installmentsSelected,
  ]);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [showEmailRequired, setShowEmailRequired] = useState(false);
  const [showInsufficientPoints, setShowInsufficientPoints] = useState(false);

  const refreshUserBalance = useCallback(async () => {
    if (displayMode === "money") return;
    try {
      const balance = await clientesApi.get(`customer/${user.cpf}/balance`);
      const userData = getCustomerFromMemory();
      if (!userData) return;
      userData.data.balance = { data: balance.data.data };
      setCustomerInMemory(userData);
      updateUserData();
    } catch (error) {
      console.error("Falha ao atualizar saldo após compra:", error);
    }
  }, [displayMode, user?.cpf, updateUserData]);

  const runFinalize = useCallback(async () => {
    if (!canFinalize) return;
    setLoadingCheckout(true);

    const couponCode =
      couponInfo?.code ||
      couponInfo?.name ||
      dataCheckout?.summary?.coupon?.code ||
      dataCheckout?.summary?.coupon?.name ||
      "";

    const customerData = {
      pagarmeId: null,
      customerUuid: user.uuid,
      createdAt: user.created_at
        ? new Date(user.created_at).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0],
      firstName: user.nome,
      lastName: user.sobrenome,
      cpf: user.cpf,
      email: user.email,
      phones: { number: user.telefone },
      address: {
        addressUuid: addressShipping?.uuid,
        streetName: addressShipping?.address_line1,
        complement: addressShipping?.address_line3,
        neighborhood: addressShipping?.address_line2,
        number: addressShipping?.number,
        cep: addressShipping?.cep,
        city: addressShipping?.city,
        state: addressShipping?.state,
        reference: addressShipping?.reference,
      },
    };

    // Re-fetch o resumo IMEDIATAMENTE antes do POST finalize.
    // Backend pode ter recalculado frete; sem isso, o totalAmount enviado fica
    // obsoleto e o backend rejeita com "Valor total não confere".
    let freshSummary = null;
    try {
      const refreshed = await pedidosApi.get(
        `v2/checkout/resumo/${channel}`,
        { params: { customerUuid: user.uuid, cep, fator: fatorFrete } },
      );
      freshSummary = refreshed.data?.data?.summary || refreshed.data?.summary;
      const normalized = {
        ...(refreshed.data?.data || refreshed.data || {}),
        cart: flattenCart(
          (refreshed.data?.data || refreshed.data || {}).cart,
        ),
      };
      setDataCheckout(normalized);
    } catch (e) {
      // Se o refresh falhar, segue com o valor que já temos em estado.
    }

    const totalSummary = Number(
      freshSummary?.total ?? dataCheckout?.summary?.total ?? 0,
    );
    // Backend pode mudar o frete entre o GET resumo da tela e o POST finalize
    // (visto em HML: mesmo CEP/cart retorna fretes diferentes). Recalculamos
    // reaisRestante com o valor mais recente para evitar "Valor total não confere".
    const refreshedReaisRestante = computePaymentFromPoints(pontosValue, {
      ...sliderContext,
      totalMoney: totalSummary,
    }).moneyValue;

    const purchaseTrack = (orderUuid) => {
      trackPurchaseMarketplaceItems(dataCheckout.cart?.items || [], {
        transactionId: orderUuid,
        value: totalSummary,
        shipping: parseFloat(dataCheckout.summary?.shipping || 0),
        coupon: couponCode || "",
      });
    };

    const clearCartItems = () => {
      const uuid = user.uuid;
      const cep = addressShipping?.cep;
      (dataCheckout.cart?.items || []).forEach((it) => {
        const sku = it.item?.sku ?? it.sku;
        if (!sku) return;
        dispatch(removeItemCart({ sku, uuid, cep }));
      });
    };

    const paymentTypeLabel =
      reaisRestante > 0 && pontosValue > 0
        ? "cartao-de-credito+pontos"
        : reaisRestante > 0
          ? "cartao-de-credito"
          : "pontos";

    try {
      // CASE 1: 100% pontos/cupom — no BRL to pay
      if (refreshedReaisRestante === 0) {
        const body = {
          totalAmount: totalSummary,
          customerData,
          paymentBRL: {
            type: "POINTS",
            amount: 0,
            installments: 1,
            save: false,
            mainCard: false,
            cardUuid: null,
            gatewayCardKey: null,
          },
        };
        if (pontosValue > 0) {
          body.paymentPoints = {
            fator: fatorFrete,
            amount: Math.round(pontosValue) || 0,
          };
        }
        if (couponCode) body.coupon = couponCode;

        trackAddPaymentInfoMarketplaceItems(
          dataCheckout.cart?.items || [],
          paymentTypeLabel,
        );

        const response = await pedidosApi.post(
          `v2/checkout/finalizar-pedido/${channel}`,
          body,
        );
        const orderUuid =
          response?.data?.data?.orderUuid || response?.data?.orderUuid;

        if (orderUuid) {
          const authorizeOrder = await pedidosApi.post(
            `v1/pedidos/${channel}/authorization/` + orderUuid,
            { authorized: true },
          );
          if (
            authorizeOrder?.data?.data?.status?.toLowerCase() === "denied"
          ) {
            navigate(`/loja/ordernotapproved`, {
              state: {
                errorMessage: "Pagamento não autorizado pelo emissor do cartão.",
              },
            });
            return;
          }
          await refreshUserBalance();
          purchaseTrack(orderUuid);
          clearCartItems();
          navigate(`/loja/orderresumestatus/${orderUuid}`);
        } else {
          navigate(`/loja/ordernotapproved`);
        }
        return;
      }

      // CASE 2: PIX (with optional points complement)
      if (paymentMethod === "pix") {
        const body = {
          totalAmount: refreshedReaisRestante,
          customerData,
          paymentBRL: { type: "PIX", amount: refreshedReaisRestante },
        };
        if (pontosValue > 0) {
          body.paymentPoints = {
            fator: fatorFrete,
            amount: Math.round(pontosValue) || 0,
          };
        }
        if (couponCode) body.coupon = couponCode;

        trackAddPaymentInfoMarketplaceItems(
          dataCheckout.cart?.items || [],
          paymentTypeLabel,
        );

        const response = await pedidosApi.post(
          `v2/checkout/finalizar-pedido/${channel}`,
          body,
        );
        const responseData = response?.data?.data || {};
        const { orderId, pixCode, pixQrCode, pixExpiresAt } = responseData;
        const orderUuid = orderId || responseData.orderUuid;

        clearCartItems();
        purchaseTrack(orderUuid);
        navigate(`/loja/pixconfirmation/${orderUuid}`, {
          state: { pixCode, pixQrCode, pixExpiresAt, totalAmount: body.totalAmount },
        });
        return;
      }

      // CASE 3: Cartão (with optional points complement)
      if (paymentMethod === "card") {
        const card = cards.find(
          (c) => (c.uuid || c.id) === selectedCardId,
        );
        const type = pontosValue > 0 ? "BOTH" : "CREDIT";

        const body = {
          totalAmount: refreshedReaisRestante,
          customerData,
          paymentBRL: {
            type,
            amount: refreshedReaisRestante,
            installments: Number(installmentsSelected) || 1,
            save: false,
            mainCard: false,
            cardUuid: null,
            gatewayCardKey: null,
          },
        };

        if (card?.uuid) {
          body.paymentBRL.cardUuid = card.uuid;
        } else if (card?.hash) {
          body.paymentBRL.gatewayCardKey = card.hash;
        }

        if (pontosValue > 0) {
          body.paymentPoints = {
            fator: fatorFrete,
            amount: Math.round(pontosValue) || 0,
          };
        }
        if (couponCode) body.coupon = couponCode;

        trackAddPaymentInfoMarketplaceItems(
          dataCheckout.cart?.items || [],
          paymentTypeLabel,
        );

        const response = await pedidosApi.post(
          `v2/checkout/finalizar-pedido/${channel}`,
          body,
        );
        const orderUuid =
          response?.data?.data?.orderUuid || response?.data?.orderUuid;

        if (orderUuid) {
          const authorizeOrder = await pedidosApi.post(
            `v1/pedidos/${channel}/authorization/` + orderUuid,
            { authorized: true },
          );
          if (
            authorizeOrder?.data?.data?.status?.toLowerCase() === "denied"
          ) {
            navigate(`/loja/ordernotapproved`, {
              state: {
                errorMessage: "Pagamento não autorizado pelo emissor do cartão.",
              },
            });
            return;
          }
          await refreshUserBalance();
          purchaseTrack(orderUuid);
          clearCartItems();
          navigate(`/loja/orderresumestatus/${orderUuid}`);
        } else {
          navigate(`/loja/ordernotapproved`);
        }
        return;
      }
    } catch (e) {
      const backendMessage =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "";
      navigate("/loja/ordernotapproved", {
        state: { errorMessage: backendMessage },
      });
    } finally {
      setLoadingCheckout(false);
    }
  }, [
    canFinalize,
    user,
    addressShipping,
    couponInfo,
    reaisRestante,
    paymentMethod,
    selectedCardId,
    installmentsSelected,
    pontosValue,
    sliderContext,
    fatorFrete,
    cards,
    dataCheckout,
    dispatch,
    navigate,
    refreshUserBalance,
  ]);

  const finalize = useCallback(() => {
    // Guard 1: user must have an email
    const userEmail = user?.email || user?.dsEmail || user?.dsEmailCliente;
    if (!userEmail) {
      setShowEmailRequired(true);
      return;
    }
    // Guard 2: pontos sufficient
    if (hasInsufficientPoints || pontosValue > userBalance) {
      setShowInsufficientPoints(true);
      return;
    }
    return runFinalize();
  }, [
    user?.email,
    user?.dsEmail,
    user?.dsEmailCliente,
    hasInsufficientPoints,
    userBalance,
    pontosValue,
    runFinalize,
  ]);

  return {
    loading,
    loadingCheckout,
    setLoadingCheckout,
    user,
    addressShipping,
    whitelabel,
    displayMode,
    paymentFlags,
    couponsEnabled,
    fatorFrete,
    channel,
    dataCheckout,
    financialSummary,
    fetchSummary,
    coupon,
    setCoupon,
    couponInfo,
    couponError,
    loadingCoupon,
    applyCoupon,
    removeCoupon,
    pontosValue,
    sliderPontos,
    sliderReais,
    handlePontosPreview,
    handlePontosCommit,
    reaisValue,
    reaisRestante,
    pontosDisponiveis,
    minPontosSlider,
    productPoints: effectiveTotalPoints,
    totalPoints,
    userBalance,
    showPointsSlider,
    totalEmReais,
    moneyFromPoints,
    paymentMethod,
    setPaymentMethod,
    cards,
    selectedCardId,
    setSelectedCardId,
    cardMgmt,
    installments,
    installmentsSelected,
    setInstallmentsSelected,
    canFinalize,
    finalize,
    showEmailRequired,
    setShowEmailRequired,
    showInsufficientPoints,
    setShowInsufficientPoints,
    hasInsufficientPoints,
    minPoints,
  };
}
